import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { grades } from "../utils/data";

export function useTests({
  grade = "VI",
  section = "A",
  initialPage = 0,
  initialLimit = 1000,
} = {}) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialLimit);
  const [count, setCount] = useState(0);

  // ✅ Cache pages
  const cacheRef = useRef({});

  // ✅ Cancel previous request
  const controllerRef = useRef(null);

  const getExams = useCallback(
    async (pageNumber = 1) => {
      const cacheKey = `${grade}-${section}-${pageNumber}-${rowsPerPage}`;

      // 🔥 Return cached data
      if (cacheRef.current[cacheKey]) {
        const cached = cacheRef.current[cacheKey];
        setExams(cached.data);
        setCount(cached.count);
        return;
      }
      try {
        setLoading(true);

        if (controllerRef.current) {
          controllerRef.current.abort();
        }

        controllerRef.current = new AbortController();

        const res = await axios(
          `${process.env.REACT_APP_ORIGIN}/api/v1/grades/${grade}/sections/${section}/exams?page=${pageNumber}&limit=${rowsPerPage}`,
          {
            headers: {
              token: window.localStorage.getItem("authToken"),
            },
            signal: controllerRef.current.signal,
          },
        );

        const data = (res.data?.data || []).filter((row) => {
          const gradeName = grades[row.grade - 1];
          return gradeName === grade;
        });

        const total = res.data?.count || 0;

        // ✅ Save in cache
        cacheRef.current[cacheKey] = {
          data,
          count: total,
        };

        setExams(data);
        setCount(total);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [grade, section, rowsPerPage],
  );

  useEffect(() => {
    getExams(page + 1);
  }, [page, rowsPerPage, getExams]);

  return {
    exams,
    loading,
    page,
    rowsPerPage,
    count,
    // actions
    setPage,
    setRowsPerPage,
    refresh: () => getExams(page + 1),
    // advanced
    clearCache: () => (cacheRef.current = {}),
  };
}
